import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { CheckCircle2, AlertTriangle, Loader2, Send, Sparkles, Mail, RefreshCw, X, Megaphone } from 'lucide-react';
import { toast } from 'sonner';
import { placementLettersBulkAPI, industryLettersAPI, studentEnumsAPI } from '../lib/api';

const STEPS = ['filters', 'preview', 'generate-result', 'send-result'];

const MODE_CONFIG = {
  placement: {
    title: 'Bulk Student Placement Letters',
    subtitle: 'Email Student Placement Confirmation Letters to every student in a cohort.',
    api: placementLettersBulkAPI,
    rowLabel: 'placements',
    actorNoun: 'students',
    recipientLabel: 'student',
  },
  industry: {
    title: 'Bulk Industry Letters',
    subtitle: 'Email per-restaurant Industry Placement Confirmation Letters for a cycle window.',
    api: industryLettersAPI,
    rowLabel: 'restaurants',
    actorNoun: 'restaurants',
    recipientLabel: 'chef',
  },
};

/**
 * Bulk letters wizard.
 *
 * Standalone usage (top-level Placements page): user picks placement/industry
 * mode and cohort filters manually.
 *
 * Preset usage (Cycle Detail Documents tab): pass `presetCycle` and
 * `presetMode`. The dialog is scoped to that cycle: filters are pre-filled
 * (year/course/type OR start/end dates), the filter inputs are hidden, and
 * every request carries `cycle_id` so the backend narrows to just this cycle.
 */
const BulkLettersDialog = ({ open, onOpenChange, onCompleted, presetCycle = null, presetMode = null }) => {
  const [mode, setMode] = useState(presetMode || 'placement');
  const [step, setStep] = useState('filters');
  const cfg = MODE_CONFIG[mode];
  const cycleId = presetCycle?.id || null;

  // Keep `mode` in lock-step with the incoming `presetMode` prop. Without
  // this, the dialog is mounted once by the parent (CycleDetail) and its
  // internal state snapshot from first mount wins even when the parent
  // toggles presetMode between 'Bulk placement' and 'Bulk industry' clicks.
  useEffect(() => {
    if (presetMode) setMode(presetMode);
  }, [presetMode]);

  // Placement-mode filters — auto-fill from the preset cycle when present.
  const [filters, setFilters] = useState({
    year_of_study: presetCycle?.year_of_study || '',
    course_length: presetCycle?.course_length || '',
    course_type: presetCycle?.course_type || '',
  });
  const [enums, setEnums] = useState({ years_of_study: [], course_lengths: [], course_types: [] });

  // Industry-mode filters — pull the date window from the preset cycle if given,
  // otherwise fall back to a sensible "this year" default.
  const today = new Date();
  const [cycleStart, setCycleStart] = useState(presetCycle?.start_date || `${today.getFullYear()}-01-01`);
  const [cycleEnd, setCycleEnd] = useState(presetCycle?.end_date || `${today.getFullYear()}-12-31`);

  const [preview, setPreview] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [generateResult, setGenerateResult] = useState(null);
  const [sendResult, setSendResult] = useState(null);

  const reset = useCallback(() => {
    setStep('filters'); setPreview(null); setSelected(new Set()); setGenerateResult(null); setSendResult(null); setBusy(false);
  }, []);
  useEffect(() => { if (open) reset(); }, [open, reset]);

  // Fetch the canonical student enum lists (year, course_length, course_type)
  useEffect(() => {
    if (!open) return;
    studentEnumsAPI.list().then((r) => setEnums(r.data)).catch(() => { /* fall back to empty */ });
  }, [open]);

  const cleanPlacementFilters = () => {
    const base = Object.fromEntries(Object.entries(filters).filter(([, v]) => v && v !== '__all__'));
    if (cycleId) base.cycle_id = cycleId;
    return base;
  };

  // Rows shown in the preview checklist (shape differs per mode).
  const rowId = (r) => (mode === 'industry' ? r.restaurant_id : r.placement_id);
  const previewRows = preview ? (mode === 'industry' ? preview.restaurants : preview.placements) || [] : [];

  const handlePreview = async () => {
    setBusy(true);
    try {
      const payload = mode === 'industry'
        ? { cycle_start: cycleStart, cycle_end: cycleEnd, ...(cycleId ? { cycle_id: cycleId } : {}) }
        : cleanPlacementFilters();
      const res = await cfg.api.preview(payload);
      setPreview(res.data);
      const rows = (mode === 'industry' ? res.data.restaurants : res.data.placements) || [];
      setSelected(new Set(rows.map((r) => (mode === 'industry' ? r.restaurant_id : r.placement_id))));
      setStep('preview');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Preview failed');
    } finally { setBusy(false); }
  };

  const selectedRows = previewRows.filter((r) => selected.has(rowId(r)));
  const selectedToCreate = selectedRows.filter((r) => !r.has_letter).length;
  const toggleRow = (id) => setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const setAll = (on) => setSelected(on ? new Set(previewRows.map(rowId)) : new Set());

  const handleGenerate = async () => {
    setBusy(true);
    try {
      const ids = [...selected];
      const payload = mode === 'industry'
        ? { cycle_start: cycleStart, cycle_end: cycleEnd, restaurant_ids: ids, ...(cycleId ? { cycle_id: cycleId } : {}) }
        : { ...cleanPlacementFilters(), placement_ids: ids };
      const res = await cfg.api.generate(payload);
      setGenerateResult(res.data);
      setStep('generate-result');
      if (res.data.created_count) toast.success(`Created ${res.data.created_count} drafts`);
      if (res.data.error_count) toast.error(`${res.data.error_count} errors during generation`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Generation failed');
    } finally { setBusy(false); }
  };

  const handleSendAll = async () => {
    setBusy(true);
    try {
      const payload = { base_url: window.location.origin };
      if (mode === 'industry') {
        Object.assign(payload, { cycle_start: cycleStart, cycle_end: cycleEnd });
      } else {
        Object.assign(payload, cleanPlacementFilters());
      }
      if (cycleId) payload.cycle_id = cycleId;
      // Only the letters belonging to the staff selection: new drafts plus the
      // existing unsigned letters of selected rows that were skipped.
      const ids = [
        ...(generateResult?.created || []).map((c) => c.contract_id),
        ...(generateResult?.skipped || []).map((s) => s.contract_id).filter(Boolean),
      ];
      if (ids.length) {
        payload.contract_ids = ids;
        payload.statuses = ['draft', 'sent', 'viewed'];
      } else {
        payload.statuses = ['draft'];
      }
      const res = await cfg.api.send(payload);
      setSendResult(res.data);
      setStep('send-result');
      if (res.data.sent_count) toast.success(`Sent ${res.data.sent_count} letters`);
      if (res.data.failed_count) toast.error(`${res.data.failed_count} sends failed`);
      onCompleted?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed');
    } finally { setBusy(false); }
  };

  // --- Shared preview stat extraction (shape differs per mode) ---
  const previewTotals = preview && (mode === 'industry' ? {
    total: preview.total_restaurants,
    with_letter: preview.with_existing_letter,
    to_create: preview.to_create,
    missing_count: preview.restaurants_missing_chef_email_count,
    missing_rows: preview.restaurants_missing_chef_email,
    missing_label: 'Restaurants missing chef email',
    missing_row_label: (r) => r.restaurant_name,
  } : {
    total: preview.total_placements,
    with_letter: preview.with_existing_letter,
    to_create: preview.to_create,
    missing_count: preview.missing_email_count,
    missing_rows: preview.students_missing_email,
    missing_label: 'Students missing email',
    missing_row_label: (r) => `${r.student_name}${r.student_number ? ` (${r.student_number})` : ''}`,
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-ica-navy flex items-center gap-2">
            {mode === 'industry' ? <Megaphone className="w-5 h-5 text-ica-navy" /> : <Sparkles className="w-5 h-5 text-ica-gold-dark" />}
            {cfg.title}
          </DialogTitle>
          <DialogDescription>{cfg.subtitle}</DialogDescription>
        </DialogHeader>

        {/* Mode toggle (filters step). A preset mode only chooses the initial tab. */}
        {step === 'filters' && (
          <div className="flex gap-2 bg-gray-50 rounded p-1 border border-gray-200" data-testid="bulk-mode-toggle">
            {['placement', 'industry'].map((m) => (
              <button
                key={m}
                type="button"
                data-testid={`bulk-mode-${m}`}
                onClick={() => { setMode(m); reset(); }}
                className={`flex-1 px-3 py-1.5 rounded text-xs font-semibold transition ${
                  mode === m ? 'bg-ica-navy text-white shadow' : 'text-gray-500 hover:text-ica-navy'
                }`}
              >
                {m === 'placement' ? 'Student placement letters' : 'Industry letters'}
              </button>
            ))}
          </div>
        )}

        {/* Stepper */}
        <ol className="flex items-center gap-2 text-xs text-gray-500" data-testid="bulk-letters-stepper">
          {[
            { id: 'filters', label: '1. Filters' },
            { id: 'preview', label: '2. Preview' },
            { id: 'generate-result', label: '3. Drafts' },
            { id: 'send-result', label: '4. Sent' },
          ].map((s) => {
            const active = STEPS.indexOf(step) >= STEPS.indexOf(s.id);
            return (
              <li key={s.id} className={`px-2 py-0.5 rounded ${active ? 'bg-ica-navy text-white' : 'bg-gray-100'}`}>{s.label}</li>
            );
          })}
        </ol>

        {/* Step 1: Filters */}
        {step === 'filters' && (
          <div className="space-y-4" data-testid="bulk-step-filters">
            {presetCycle ? (
              <div className="rounded-md border border-ica-navy/20 bg-ica-navy/5 p-3 text-sm space-y-1" data-testid="bulk-preset-cycle-summary">
                <div className="text-xs uppercase tracking-wide text-ica-navy/70 font-semibold">Scoped to cycle</div>
                <div className="font-semibold text-ica-navy">{presetCycle.name}</div>
                <div className="text-xs text-gray-600">
                  {mode === 'placement' ? (
                    <>Cohort: {presetCycle.year_of_study} · {presetCycle.course_length} · {presetCycle.course_type}</>
                  ) : (
                    <>Window: {presetCycle.start_date} → {presetCycle.end_date}</>
                  )}
                </div>
                <div className="text-[11px] text-gray-500">
                  Only {mode === 'placement' ? 'placements' : 'restaurants with placements'} in this cycle will be included. Existing non-voided letters are skipped.
                </div>
              </div>
            ) : mode === 'placement' ? (
              <>
                <p className="text-sm text-gray-600">Scope by cohort. Leave a field empty to include all.</p>
                <div className="grid grid-cols-2 gap-3">
                  <FilterSelect label="Year of study" testId="bulk-filter-year" value={filters.year_of_study} options={enums.years_of_study}
                    onChange={(v) => setFilters({ ...filters, year_of_study: v })} placeholderAll="All years" />
                  <FilterSelect label="Course length" testId="bulk-filter-course-length" value={filters.course_length} options={enums.course_lengths}
                    onChange={(v) => setFilters({ ...filters, course_length: v })} placeholderAll="Any length" />
                  <div className="col-span-2">
                    <FilterSelect label="Course type" testId="bulk-filter-course-type" value={filters.course_type} options={enums.course_types}
                      onChange={(v) => setFilters({ ...filters, course_type: v })} placeholderAll="Any type" />
                  </div>
                </div>
                <p className="text-xs text-gray-400">
                  Only active and pending placements are processed. Placements that already have a non-voided letter are skipped automatically.
                </p>
              </>
            ) : (
              <>
                <p className="text-sm text-gray-600">
                  Pick the cycle window. Letters are generated per restaurant for any placement that overlaps this window.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Cycle start</Label>
                    <Input data-testid="bulk-industry-start" type="date" value={cycleStart} onChange={(e) => setCycleStart(e.target.value)} />
                  </div>
                  <div>
                    <Label>Cycle end</Label>
                    <Input data-testid="bulk-industry-end" type="date" value={cycleEnd} onChange={(e) => setCycleEnd(e.target.value)} />
                  </div>
                </div>
                <p className="text-xs text-gray-400">
                  Restaurants that already have a non-voided industry letter for this cycle are skipped automatically.
                </p>
              </>
            )}
          </div>
        )}

        {/* Step 2: Preview */}
        {step === 'preview' && previewTotals && (
          <div className="space-y-3" data-testid="bulk-step-preview">
            <div className="grid grid-cols-2 gap-3">
              <Stat label={`Matching ${cfg.rowLabel}`} value={previewTotals.total} testId="stat-total" />
              <Stat label="Already have a letter" value={previewTotals.with_letter} testId="stat-existing" tone="amber" />
              <Stat label="Will be created" value={previewTotals.to_create} testId="stat-tocreate" tone="emerald" />
              <Stat label={previewTotals.missing_label} value={previewTotals.missing_count} testId="stat-missing" tone={previewTotals.missing_count ? 'red' : 'gray'} />
            </div>
            {previewTotals.missing_count > 0 && (
              <div className="bg-red-50 border border-red-200 rounded p-3 text-xs space-y-1">
                <p className="font-semibold text-red-700 flex items-center gap-1">
                  <AlertTriangle className="w-3.5 h-3.5" /> These will fail at send-time:
                </p>
                <ul className="ml-4 list-disc text-red-700">
                  {previewTotals.missing_rows.slice(0, 8).map((s, idx) => (
                    <li key={idx}>{previewTotals.missing_row_label(s)}</li>
                  ))}
                  {previewTotals.missing_count > 8 && <li>… and {previewTotals.missing_count - 8} more</li>}
                </ul>
                <p className="text-red-600">You can still generate drafts now and email manually later.</p>
              </div>
            )}
            {previewTotals.to_create === 0 && (
              <p className="text-sm text-gray-600 bg-amber-50 border border-amber-200 rounded p-2">
                Nothing new to create — every matching {cfg.rowLabel.replace(/s$/, '')} already has a letter. You can still send existing drafts for the ticked rows.
              </p>
            )}

            {previewRows.length > 0 && (
              <div className="rounded border border-gray-200" data-testid="bulk-preview-checklist">
                <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b text-xs">
                  <span className="font-semibold text-ica-navy">
                    Choose {cfg.actorNoun} · {selected.size} of {previewRows.length} ticked
                  </span>
                  <span className="flex gap-2">
                    <button type="button" data-testid="bulk-select-all" className="underline text-gray-600 hover:text-ica-navy" onClick={() => setAll(true)}>All</button>
                    <button type="button" data-testid="bulk-select-none" className="underline text-gray-600 hover:text-ica-navy" onClick={() => setAll(false)}>None</button>
                    <button type="button" data-testid="bulk-select-new" className="underline text-gray-600 hover:text-ica-navy"
                      onClick={() => setSelected(new Set(previewRows.filter((r) => !r.has_letter).map(rowId)))}>Only without letter</button>
                  </span>
                </div>
                <ul className="max-h-64 overflow-y-auto divide-y text-sm">
                  {previewRows.map((r) => {
                    const id = rowId(r);
                    return (
                      <li key={id}>
                        <label className="flex items-center gap-3 px-3 py-1.5 cursor-pointer hover:bg-gray-50">
                          <input
                            type="checkbox"
                            data-testid={`bulk-row-${id}`}
                            className="h-4 w-4 accent-ica-navy"
                            checked={selected.has(id)}
                            onChange={() => toggleRow(id)}
                          />
                          <span className="flex-1 min-w-0 truncate">
                            {mode === 'industry'
                              ? <>{r.restaurant_name} <span className="text-gray-400 text-xs">· {r.student_count} student{r.student_count === 1 ? '' : 's'}</span></>
                              : <>{r.student_name} <span className="text-gray-400 text-xs">{r.student_number ? `· ${r.student_number} ` : ''}· {r.restaurant_name}</span></>}
                          </span>
                          {!r.has_email && <span className="text-[10px] font-semibold uppercase text-red-600" title="No email on file">no email</span>}
                          {r.has_letter && <span className="text-[10px] font-semibold uppercase text-amber-700" data-testid={`bulk-row-has-letter-${id}`}>has {r.letter_status || 'letter'}</span>}
                        </label>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Step 3: Generate result */}
        {step === 'generate-result' && generateResult && (
          <div className="space-y-3" data-testid="bulk-step-generate-result">
            <div className="grid grid-cols-3 gap-3">
              <Stat label="Created" value={generateResult.created_count} testId="stat-created" tone="emerald" />
              <Stat label="Skipped" value={generateResult.skipped_count} testId="stat-skipped" tone="amber" />
              <Stat label="Errors" value={generateResult.error_count} testId="stat-errors" tone={generateResult.error_count ? 'red' : 'gray'} />
            </div>
            <DetailList title="Created drafts" rows={generateResult.created} keyFn={(r) => r.contract_id}
              render={(r) => r.student_name || r.restaurant_name} />
            {generateResult.skipped.length > 0 && (
              <DetailList title="Skipped" rows={generateResult.skipped} keyFn={(r) => r.contract_id || r.placement_id || r.restaurant_id}
                render={(r) => `${r.student_name || r.restaurant_name} — ${r.reason}`} tone="amber" />
            )}
            {generateResult.errors.length > 0 && (
              <DetailList title="Errors" rows={generateResult.errors} keyFn={(r) => r.placement_id || r.restaurant_id}
                render={(r) => `${r.student_name || r.restaurant_name} — ${r.error}`} tone="red" />
            )}
          </div>
        )}

        {/* Step 4: Send result */}
        {step === 'send-result' && sendResult && (
          <div className="space-y-3" data-testid="bulk-step-send-result">
            <div className="grid grid-cols-3 gap-3">
              <Stat label="Targeted" value={sendResult.total_targeted} testId="stat-targeted" />
              <Stat label="Sent" value={sendResult.sent_count} testId="stat-sent" tone="emerald" />
              <Stat label="Failed" value={sendResult.failed_count} testId="stat-failed" tone={sendResult.failed_count ? 'red' : 'gray'} />
            </div>
            <DetailList title="Sent" rows={sendResult.sent} keyFn={(r) => r.contract_id}
              render={(r) => `${r.student_name || r.restaurant_name} → ${r.recipient}`} tone="emerald" />
            {sendResult.failed.length > 0 && (
              <DetailList title="Failed" rows={sendResult.failed} keyFn={(r) => r.contract_id}
                render={(r) => `${r.student_name || r.restaurant_name} — ${r.reason}`} tone="red" />
            )}
          </div>
        )}

        <DialogFooter className="gap-2 flex-wrap">
          {step === 'filters' && (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button data-testid="bulk-preview-btn" onClick={handlePreview} disabled={busy} >
                {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                Preview cohort
              </Button>
            </>
          )}
          {step === 'preview' && (
            <>
              <Button variant="outline" onClick={() => setStep('filters')}>Back</Button>
              <Button data-testid="bulk-generate-btn" onClick={handleGenerate} disabled={busy || selected.size === 0} >
                {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Sparkles className="w-4 h-4 mr-1" />}
                {selectedToCreate > 0
                  ? `Generate ${selectedToCreate} draft${selectedToCreate === 1 ? '' : 's'} for ${selected.size} selected`
                  : `Continue with ${selected.size} selected`}
              </Button>
            </>
          )}
          {step === 'generate-result' && (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Close (review later)</Button>
              {(() => {
                const newCount = generateResult?.created_count ?? 0;
                const existing = (generateResult?.skipped || []).filter((s) => s.contract_id).length;
                const sendable = newCount + existing;
                return (
                  <Button data-testid="bulk-send-all-btn" onClick={handleSendAll} disabled={busy || sendable === 0}>
                    {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
                    {newCount > 0 && existing > 0
                      ? `Send ${sendable} selected letters (${newCount} new, ${existing} existing)`
                      : newCount > 0
                        ? `Send all ${newCount} new draft${newCount === 1 ? '' : 's'} now`
                        : `Send ${existing} existing unsigned letter${existing === 1 ? '' : 's'}`}
                  </Button>
                );
              })()}
            </>
          )}
          {step === 'send-result' && (
            <Button onClick={() => onOpenChange(false)} >
              <X className="w-4 h-4 mr-1" /> Done
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const FilterSelect = ({ label, testId, value, options, onChange, placeholderAll }) => (
  <div>
    <Label>{label}</Label>
    <Select value={value || '__all__'} onValueChange={(v) => onChange(v === '__all__' ? '' : v)}>
      <SelectTrigger data-testid={testId}><SelectValue placeholder={placeholderAll} /></SelectTrigger>
      <SelectContent>
        <SelectItem value="__all__">{placeholderAll}</SelectItem>
        {(options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
      </SelectContent>
    </Select>
  </div>
);

const TONE_CLASSES = {
  gray: 'bg-gray-50 border-gray-200 text-gray-700',
  emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700',
  amber: 'bg-amber-50 border-amber-200 text-amber-700',
  red: 'bg-red-50 border-red-200 text-red-700',
};

const Stat = ({ label, value, testId, tone = 'gray' }) => (
  <div data-testid={testId} className={`rounded border ${TONE_CLASSES[tone]} p-3 text-center`}>
    <div className="text-2xl font-bold leading-none">{value}</div>
    <div className="text-[11px] uppercase tracking-wide mt-1">{label}</div>
  </div>
);

const DetailList = ({ title, rows, render, keyFn, tone = 'emerald' }) => {
  if (!rows?.length) return null;
  const headIcon = tone === 'red' ? <AlertTriangle className="w-3.5 h-3.5" /> : tone === 'emerald' ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Mail className="w-3.5 h-3.5" />;
  return (
    <details className={`rounded border p-2 ${TONE_CLASSES[tone]}`} open={rows.length <= 5}>
      <summary className="cursor-pointer text-xs font-semibold flex items-center gap-1">
        {headIcon} {title} ({rows.length})
      </summary>
      <ul className="mt-2 space-y-1 max-h-40 overflow-y-auto text-xs ml-5 list-disc">
        {rows.slice(0, 50).map((r) => <li key={keyFn(r)}>{render(r)}</li>)}
        {rows.length > 50 && <li>… and {rows.length - 50} more</li>}
      </ul>
    </details>
  );
};

export default BulkLettersDialog;
